import { createStoredZip, type ClientZipFile } from './zipCore'

export type { ClientZipFile } from './zipCore'

export type ClientZipRemoteFile = {
  name: string
  url: string
}

export { createStoredZip }

export type ZipBuildProgress = {
  phase: 'downloading' | 'assembling'
  completed: number
  total: number
  currentFile?: string
}

export class ZipDownloadCancelledError extends Error {
  constructor() {
    super('ZIP download cancelled.')
  }
}

export async function createStoredZipFromUrls(files: ClientZipRemoteFile[], fetchImpl: typeof fetch = fetch) {
  const resolved: ClientZipFile[] = []
  for (const file of files) {
    const response = await fetchImpl(file.url)
    if (!response.ok) throw new Error(`Could not fetch ${file.name}.`)
    resolved.push({ name: file.name, data: await response.arrayBuffer() })
  }
  return createStoredZip(resolved)
}

function saveZipBytes(zipBytes: Uint8Array, filename: string) {
  const blob = new Blob([zipBytes], { type: 'application/zip' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function startZipDownloadFromWorker(
  files: ClientZipRemoteFile[],
  filename: string,
  onProgress?: (progress: ZipBuildProgress) => void
) {
  let settled = false
  let rejectDone: ((reason?: unknown) => void) | null = null
  const worker = new Worker(new URL('../workers/zipWorker.ts', import.meta.url), { type: 'module' })

  const done = new Promise<void>((resolve, reject) => {
    rejectDone = reject

    worker.onmessage = (event: MessageEvent<any>) => {
      const message = event.data
      if (message.type === 'progress') {
        onProgress?.(message as ZipBuildProgress)
        return
      }
      if (message.type === 'complete') {
        settled = true
        saveZipBytes(new Uint8Array(message.zipBytes), filename)
        worker.terminate()
        resolve()
        return
      }
      if (message.type === 'error') {
        settled = true
        worker.terminate()
        reject(new Error(String(message.message || 'Could not build ZIP.')))
      }
    }

    worker.onerror = () => {
      settled = true
      worker.terminate()
      reject(new Error('Could not build ZIP.'))
    }
  })

  worker.postMessage({ type: 'build', files })

  return {
    cancel() {
      if (settled) return
      settled = true
      worker.terminate()
      rejectDone?.(new ZipDownloadCancelledError())
    },
    done
  }
}

export async function downloadZipFromUrls(files: ClientZipRemoteFile[], filename: string, fetchImpl: typeof fetch = fetch) {
  const zipBytes = await createStoredZipFromUrls(files, fetchImpl)
  saveZipBytes(zipBytes, filename)
}
